/**
 * The canonical cerebrum shape function.
 *
 * Shared by the wireframe geometry builder and the particle containment test
 * so the two can never drift apart. Everything here is pure math on plain
 * numbers — no Three.js, no DOM — so it is directly unit-testable.
 *
 * Coordinate convention: +x = right, +y = superior (up), +z = anterior (front).
 *
 * ## Why a signed distance field rather than a displaced sphere
 *
 * The obvious approach — take a sphere and push vertices in/out radially — cannot
 * produce a recognizable brain. The temporal lobe is a genuine separate
 * downward-and-forward projection, and the lateral (Sylvian) fissure is the deep
 * crease between it and the frontal/parietal mass. Carving that fissure as a
 * radial displacement moves the surface sideways (along x), which is invisible
 * in lateral silhouette — exactly the angle a brain is most recognizable from.
 *
 * Instead the cerebrum is modelled as a smooth union of anatomical lobe volumes.
 * The Sylvian fissure then emerges for free, as the concave seam where the
 * temporal lobe blends into the mass above it, and it reads correctly from every
 * angle because it is real geometry rather than a surface decoration.
 */

/** Blend radius for the smooth union between lobes. Sets how soft each seam is. */
const LOBE_BLEND = 0.1;

/** Tighter blend across the midline so the interhemispheric fissure stays crisp. */
const MIDLINE_BLEND = 0.03;

interface Lobe {
  /** Center, given for the RIGHT hemisphere; mirrored onto the left. */
  center: [number, number, number];
  radii: [number, number, number];
}

/**
 * The lobe volumes making up one cerebral hemisphere.
 *
 * Proportions follow an adult cerebrum: longest anterior-posterior (~165 mm),
 * then wide (~140 mm), then shortest superior-inferior (~95 mm). The temporal
 * lobe deliberately overlaps the dorsal mass only modestly — that partial
 * overlap is what creates the Sylvian fissure between them.
 */
const LOBES: Lobe[] = [
  // Frontal-parietal dorsal mass: the bulk of the hemisphere.
  { center: [0.32, 0.14, 0.05], radii: [0.5, 0.52, 0.92] },
  // Frontal pole: rounded anterior fullness.
  { center: [0.28, 0.04, 0.58], radii: [0.38, 0.42, 0.4] },
  // Occipital pole: tapers posteriorly.
  { center: [0.26, -0.04, -0.7], radii: [0.33, 0.35, 0.32] },
  // Temporal lobe: projects downward and forward, below the Sylvian fissure.
  { center: [0.44, -0.34, 0.2], radii: [0.26, 0.21, 0.47] },
];

/** Hermite smoothstep. Works with edge0 > edge1 to produce an inverted ramp. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Polynomial smooth minimum — the standard soft-union operator for SDFs. */
function smoothMin(a: number, b: number, k: number): number {
  const h = Math.max(0, k - Math.abs(a - b)) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

/** Signed distance to an axis-aligned ellipsoid (standard bounded approximation). */
function sdEllipsoid(
  px: number,
  py: number,
  pz: number,
  center: [number, number, number],
  radii: [number, number, number],
): number {
  const qx = (px - center[0]) / radii[0];
  const qy = (py - center[1]) / radii[1];
  const qz = (pz - center[2]) / radii[2];

  const k0 = Math.sqrt(qx * qx + qy * qy + qz * qz);
  if (k0 < 1e-6) return -Math.min(radii[0], radii[1], radii[2]);

  const nx = qx / radii[0];
  const ny = qy / radii[1];
  const nz = qz / radii[2];
  const k1 = Math.sqrt(nx * nx + ny * ny + nz * nz);

  return (k0 * (k0 - 1)) / Math.max(k1, 1e-6);
}

/**
 * Cortical folding (gyri and sulci).
 *
 * Evaluated in Cartesian space rather than in spherical angles: angle-based
 * noise converges at the poles and bands visibly there, whereas summed
 * sinusoids of Cartesian coordinates stay locally isotropic across the surface.
 * Deliberately chunky — a few deep folds read far better in wireframe than many
 * shallow ones, which just alias against the mesh triangulation.
 */
function gyralDisplacement(px: number, py: number, pz: number): number {
  let n = 0;
  n += Math.sin(px * 12.0 + py * 6.5) * Math.cos(pz * 9.5 - py * 5.0);
  n += Math.sin(pz * 11.0 + px * 5.5) * Math.cos(py * 10.0 + pz * 4.5) * 0.85;
  n += Math.sin(py * 13.5 - pz * 7.0) * Math.cos(px * 11.5 + py * 3.5) * 0.7;
  return (n / 2.55) * 0.055;
}

/**
 * Signed distance to the cerebrum surface: negative inside, positive outside.
 *
 * `includeGyri` is disabled for containment tests so particles do not flicker in
 * and out of the volume at fold boundaries.
 */
export function cerebrumSDF(px: number, py: number, pz: number, includeGyri = true): number {
  // Mirror onto the right hemisphere so the shape is exactly symmetric, then
  // reintroduce the midline separately below.
  const mx = Math.abs(px);

  let d = sdEllipsoid(mx, py, pz, LOBES[0].center, LOBES[0].radii);
  for (let i = 1; i < LOBES.length; i++) {
    d = smoothMin(d, sdEllipsoid(mx, py, pz, LOBES[i].center, LOBES[i].radii), LOBE_BLEND);
  }

  // The interhemispheric fissure: a slab of material removed along the midline,
  // deep on the superior surface and closing toward the base where the
  // hemispheres join at the corpus callosum.
  //
  // Smooth subtraction is smoothMax(d, -slab), and smoothMax(a, b) is
  // -smoothMin(-a, -b) — so subtracting the slab whose own field is
  // (mx - halfWidth) gives -smoothMin(-d, mx - halfWidth).
  // Kept narrow and confined to the upper cerebrum: the real fissure is a few
  // millimetres across, and the hemispheres remain joined below it. Cutting it
  // wide or carrying it too far down splits the brain into two separate lobes.
  //
  // The slab keeps a FIXED half-width and is instead gated by height: shrinking
  // its width to zero lower down would degenerate it into the plane x=0, and the
  // smooth-subtract blend would then erode a groove along the entire midline at
  // every height. Pushing the slab far outside the volume instead leaves the
  // field untouched where no fissure belongs.
  const noFissureBelow = 1 - smoothstep(0.05, 0.4, py);
  const fissureSlab = mx - 0.025 + noFissureBelow * 10;
  d = -smoothMin(-d, fissureSlab, MIDLINE_BLEND);

  if (includeGyri) {
    d -= gyralDisplacement(px, py, pz);
  }

  return d;
}

/**
 * Distance from the origin to the cerebrum surface along a unit direction,
 * found by bisection on the SDF. The lobe volumes all enclose the origin
 * region, so every ray crosses the surface exactly once.
 */
export function cerebrumSurfaceRadius(dx: number, dy: number, dz: number, includeGyri = true): number {
  let lo = 0.0;
  let hi = 2.0;

  for (let i = 0; i < 26; i++) {
    const mid = (lo + hi) * 0.5;
    if (cerebrumSDF(dx * mid, dy * mid, dz * mid, includeGyri) < 0) lo = mid;
    else hi = mid;
  }

  return (lo + hi) * 0.5;
}

/** Returns the cerebrum surface point along a given unit direction. */
export function cerebrumSurfacePoint(
  dx: number,
  dy: number,
  dz: number,
  includeGyri = true,
): [number, number, number] {
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (length < 1e-6) return [0, 0, 0];

  const ux = dx / length;
  const uy = dy / length;
  const uz = dz / length;
  const r = cerebrumSurfaceRadius(ux, uy, uz, includeGyri);

  return [ux * r, uy * r, uz * r];
}

/**
 * Containment test for the cerebrum volume, evaluated directly against the same
 * field the visible mesh is built from. `margin` shrinks the volume slightly so
 * particles stay visibly inside the wireframe rather than poking through it.
 */
export function isInsideCerebrum(x: number, y: number, z: number, margin = 0.04): boolean {
  return cerebrumSDF(x, y, z, false) < -margin;
}
